/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import React from 'react';
import { SurveyWorkflowRoles } from '../../../../../common/src/domain/survey/surveyWorkflowEmployee/SurveyWorkflowRoles';
import { useTranslate } from '../../../mixins/translate/translate';
import { openApiFetchDelete, openApiFetchPost } from '../../../store/openApiFetch';
import { useOpenApiFetch } from '../../../store/useOpenApiFetch';
import { EditPanelFilterSelectFromEndpoint } from '../../common/editPanel/EditPanelFilterSelectFromEndpoint';
import { IconButton } from '../../common/iconButtons/IconButton';
import { IconRemoveButton } from '../../common/iconButtons/IconRemoveButton';
import {
    EditPanelFilterSelect,
    EditPanelInput,
    EditPanelSwitch,
    PanelBody,
    PanelTable,
    TableBody,
    TableData,
    TableHead,
    TableHeadRow,
    TableHeader,
    TableRow,
} from '../../elements';
import { EnumLabel } from './EnumLabel';
import { SurveyEditCollapsiblePanel } from './SurveyEditCollapsiblePanel';
import { UserLabel } from './UserLabel';

type SurveyEditWorkflowAssignmentBodyState = {
    remarks: string;
    userId: string | null;
    workflowRole: SurveyWorkflowRoles | null;
    shouldBeNotified: boolean;
};

function EnumEditPanelFilterSelect<EnumType>(props: {
    options: EnumType[];
    value: EnumType | null;
    onChange: (value: EnumType | null) => void;
    translateKey: string;
    name: string;
}) {
    const { options, value, onChange, translateKey, name } = props;
    const translate = useTranslate();
    return (
        <EditPanelFilterSelect
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            options={options.map((i) => ({ value: i as string, text: translate(`${translateKey}.${i}`) })) ?? []}
            value={value}
            handleChange={(newValue) => onChange(newValue as EnumType)}
            name={name}
        />
    );
}

function AddWorkflowEmployee(props: { surveyId: string; onAdd: () => void }) {
    const { surveyId } = props;

    const [state, setState] = React.useState<SurveyEditWorkflowAssignmentBodyState>({
        userId: null,
        workflowRole: null,
        remarks: '',
        shouldBeNotified: false,
    });

    async function save() {
        const { userId, workflowRole, remarks, shouldBeNotified } = state;
        if (userId !== null && workflowRole !== null) {
            await openApiFetchPost('/api/surveys/{surveyId}/workflowEmployees', { surveyId }, { userId, workflowRole, remarks, shouldBeNotified });
            props.onAdd();
            setState({ remarks: '', userId: null, workflowRole: null, shouldBeNotified: false });
        }
    }

    return (
        <TableRow key="new">
            <TableData>
                <EditPanelFilterSelectFromEndpoint
                    name="userId"
                    value={state.userId}
                    onChange={(value) => {
                        const newValue = value === 'null' ? null : value;
                        setState((i) => ({ ...i, userId: newValue }));
                    }}
                    url={`/api/surveys/${surveyId}/assignment/allowedUsers`}
                    mapResponseToOptions={(response: { json: { id: string; firstName: string; lastName: string }[] }) =>
                        response.json.map((i) => ({ text: `${i.firstName} ${i.lastName}`, value: i.id }))
                    }
                />
            </TableData>
            <TableData>
                <EnumEditPanelFilterSelect
                    options={[
                        SurveyWorkflowRoles.AccountManager,
                        SurveyWorkflowRoles.Inspector,
                        SurveyWorkflowRoles.Pilot,
                        SurveyWorkflowRoles.Reporter,
                        SurveyWorkflowRoles.Validator,
                    ]}
                    translateKey="surveyWorkflowRoles"
                    value={state.workflowRole}
                    onChange={(value) => {
                        setState((i) => ({ ...i, workflowRole: value }));
                    }}
                    name="workflowRole"
                />
            </TableData>
            <TableData>
                <EditPanelSwitch
                    value={state.shouldBeNotified}
                    name="shouldBeNotified"
                    handleChange={(value) => {
                        setState((i) => ({ ...i, shouldBeNotified: value }));
                    }}
                ></EditPanelSwitch>
            </TableData>
            <TableData>
                <EditPanelInput
                    value={state.remarks}
                    name="remarks"
                    handleChange={(value) => {
                        setState((i) => ({ ...i, remarks: value }));
                    }}
                />
            </TableData>
            <TableData>
                <IconButton iconClass="fa-save" onClick={save} primary />
            </TableData>
        </TableRow>
    );
}

export function SurveyEditWorkflowEmployeesEdit(props: { surveyId: string }) {
    const translate = useTranslate();
    const { data: workflowEmployeesData, reload } = useOpenApiFetch(
        '/api/surveys/{surveyId}/workflowEmployees',
        'get',
        { surveyId: props.surveyId },
        [],
    );

    async function update(surveyWorkflowEmployeeId: string, shouldBeNotified: boolean) {
        await openApiFetchPost(
            '/api/surveys/{surveyId}/workflowEmployees/{workflowEmployeeId}',
            { surveyId: props.surveyId, workflowEmployeeId: surveyWorkflowEmployeeId },
            { shouldBeNotified },
        );
        await reload();
    }

    async function deleteItem(surveyWorkflowEmployeeId: string) {
        await openApiFetchDelete('/api/surveys/{surveyId}/workflowEmployees/{workflowEmployeeId}', {
            surveyId: props.surveyId,
            workflowEmployeeId: surveyWorkflowEmployeeId,
        });
        await reload();
    }

    if (!workflowEmployeesData) {
        return null;
    }

    return (
        <PanelBody isForm={true}>
            <PanelTable table-condensed={true}>
                <TableHead>
                    <TableHeadRow>
                        <TableHeader>{translate('common.name')}</TableHeader>
                        <TableHeader>{translate('common.role')}</TableHeader>
                        <TableHeader>{translate('workflow.notify')}</TableHeader>
                        <TableHeader>{translate('common.remarks')}</TableHeader>
                        <TableHeader></TableHeader>
                    </TableHeadRow>
                </TableHead>
                <TableBody>
                    {workflowEmployeesData.items.map((item) => (
                        <TableRow key={item.id}>
                            <TableData>
                                <UserLabel user={item.user} />
                            </TableData>
                            <TableData>
                                <EnumLabel value={item.workflowRole} enumName="surveyWorkflowRoles" />
                            </TableData>
                            <TableData>
                                <EditPanelSwitch
                                    value={item.shouldBeNotified}
                                    name={`shouldBeNotified${item.id}`}
                                    handleChange={async () => update(item.id, !item.shouldBeNotified)}
                                ></EditPanelSwitch>
                            </TableData>
                            <TableData>{item.remarks}</TableData>
                            <TableData>
                                <IconRemoveButton
                                    onClick={async () => deleteItem(item.id)}
                                    removeQuestion={translate('common.confirmDelete', `${item.user.firstName} ${item.user.lastName}`)}
                                ></IconRemoveButton>
                            </TableData>
                        </TableRow>
                    ))}
                    <AddWorkflowEmployee surveyId={props.surveyId} onAdd={reload} />
                </TableBody>
            </PanelTable>
        </PanelBody>
    );
}

export function SurveyEditWorkflowEmployees(props: { surveyId: string }) {
    const { surveyId } = props;
    const translate = useTranslate();

    return (
        <SurveyEditCollapsiblePanel initialIsClosed={true} headerText={translate('workflow.involvedPersons')}>
            <SurveyEditWorkflowEmployeesEdit surveyId={surveyId} />
        </SurveyEditCollapsiblePanel>
    );
}
